import React from 'react';
import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { mediaBreakpoint } from '@services/responsiveProvider';
import { textH1, textH2, textH3, textH4, textLink, textP, textPBold } from '@features/ui/styles/textStyles';
import { selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import EventTimeSentence, { EventTimeContext } from '@features/eventDetail/components/EventTimeSentence';
import { getQueryEventId } from '@features/eventDetail/utils';

// To avoid "window undefined" from `leaflet`
// TODO: https://nextjs.org/docs/pages/building-your-application/optimizing/lazy-loading#with-no-ssr, is used many times
const EventMap = dynamic(import('@features/eventDetail/components/EventMap'), { ssr: false });

const EventDescription = () => {
  const router = useRouter();
  const event = useSelector(selectEventDetail(getQueryEventId(router.query)));
  const { t } = useTranslation();
  const startDate = event.dateFrom ?? event.date;
  const endDate = event.dateTo ?? event.date;

  /*
    ⚠️ ⚠️ ⚠️
    So the description content comes from the backoffice wyswig editor which we don't
    have any control on it. To have the style applied we need to interpet the html inside
    but this exposes us to potential XSS attacks. Therefore we use DOMPurify to sanitize.

    more info:
    * https://developer.mozilla.org/en-US/docs/Web/API/Element/innerHTML
    As said in this article, HTML5 doesn't allow script tags inside innerHTML method,
    but we can still have attacks with "<img src='x' onerror='alert(1)'>" for instance.
    * https://pragmaticwebsecurity.com/articles/spasecurity/react-xss-part2.html
  */

  /*
    Fix bad data from wyswig

    When you set the target='_blank' in the wyswig the rel is not set
    It's not safe to use this without the rel : https://web.dev/external-anchors-use-rel-noopener/
    So we inject a customCheck value before the sanitize remove the target='_blank'
  */
  DOMPurify.addHook('beforeSanitizeAttributes', (node) => {
    if ('target' in node && node['target'] === '_blank') {
      node.setAttribute('data-target', 'blank');
    }
  });
  // Then after the sanitize we re-inject the right attribute
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.hasAttribute('data-target')) {
      node.setAttribute('rel', 'noreferrer');
      node.setAttribute('target', '_blank');
      node.removeAttribute('data-target');
    }
  });
  const clean = DOMPurify.sanitize(event.description ?? '');

  return (
    <Container>
      <DescriptionTitle>{t('eventDescription_descriptionTitle.message')}</DescriptionTitle>
      <EventTimeSentence
        startDate={startDate}
        endDate={endDate}
        context={EventTimeContext.DESCRIPTION}
      />
      {/* eslint-disable-next-line react/no-danger */}
      <Content dangerouslySetInnerHTML={{ __html: clean }} />
      {event.gps && (
        <>
          <MapTitle>{t('eventDescription_mapTitle.message')}</MapTitle>
          <EventMap
            latitude={event.gps.latitude}
            longitude={event.gps.longitude}
          />
        </>
      )}
    </Container>
  );
};

const MapTitle = styled.h3`
  ${textH3};
  margin-top: 40px;
  margin-bottom: 20px;
`;

const DescriptionTitle = styled.h2`
  ${textH2};
  margin-bottom: 20px;
`;

const Content = styled.div`
  margin-top: 20px;

  img {
    max-width: 100%;
    height: auto;
  }

  ${mediaBreakpoint.tablet} {
    overflow-x: scroll;
  }
  color: var(--color-grey4);

  strong {
    ${textPBold};
  }

  *, p {
    ${textP};
  }

  & a {
    ${textLink};
    color: var(--color-orange);
  }

  & h1, & h1 span {
    ${textH1};
  }

  & h2, & h2 span {
    ${textH2};
  }

  & h3, & h3 span {
    ${textH3};
  }

  & h4, & h4 span {
    ${textH4};
  }
`;

export default EventDescription;

const Container = styled.div`
  width: 100%;
`;
